// REACT
import { useState } from "react";
import { CircularProgress, Grid, InputAdornment } from "@mui/material";

// COMPONENTS
import { FormInput, InputStyleType } from "../../input/FormInput";

// STYLES
import {
  StyledExplainerTextTypography,
  StyledFormContainer,
  StyledFormHelperText,
  StyledSecurityNoticeTypography,
  StyledStepHeaderTypography,
} from "../common/styles/CryptoWithdrawalStyled";

// UTILS
import { truncateEmail } from "src/utils/helpers/EmailTruncate";
import { getSessionItem } from "src/utils/SessionManagement";

// REDUX
import {
  useCheckWithdrawalEmailCodeMutation,
  useResendWithdrawalEmailCodeMutation,
} from "src/store/features/api/generated";
import {
  setError,
  setEmailCode,
  set2FACode,
  setIsEmailCodeSuccess,
} from "src/store/features/withdrawal.slice";
import { useAppDispatch, useAppSelector } from "src/store/store";

// ICONS
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// AMPLITUDE
import { trackEvent } from "src/Amplitude";

// TYPES
import { Token } from "./CryptoWithdrawalModal";

interface ICryptoWithdrawalVerification {
  token: Token | null;
  withdrawalAddress: string | null;
  withdrawalAmount: number;
  isLoading: boolean;
}

/**
 * The component that handles the verification of a crypto withdrawal
 *
 * @function
 * @param {Token | null} token - The token to withdraw
 * @param {string | null} withdrawalAddress - The withdrawal address
 * @param {number} withdrawalAmount - The withdrawal amount
 * @param {function} onNextClick - The function to handle the next click
 *
 * @returns {React.FC<ICryptoWithdrawalVerification>} - The component that handles the verification of the withdrawal
 */
export const CryptoWithdrawalStageVerification = ({
  token,
  withdrawalAddress,
  withdrawalAmount,
  isLoading,
}: ICryptoWithdrawalVerification): JSX.Element => {
  const [emailVerified, setEmailVerified] = useState<Boolean>(false);
  const [sendEmail] = useResendWithdrawalEmailCodeMutation();
  const [resendClicked, setResendClicked] = useState(false);

  const currentEmail = getSessionItem("email");

  const dispatch = useAppDispatch();

  const { emailCode, code2fa } = useAppSelector((state) => state.withdrawals);

  const [
    checkEmailCode,
    { isError: checkEmailIsError, isSuccess: checkEmailSuccess, isLoading: emailCheckLoading },
  ] = useCheckWithdrawalEmailCodeMutation();

  const handleEmailChange = async (e: any) => {
    dispatch(setError(""));
    const inputCode = e.target.value;
    dispatch(setEmailCode(inputCode));
    if (e.target.value.length === 6) {
      try {
        await checkEmailCode({
          withdrawalVerificationDto: {
            emailCode: e.target.value,
          },
        })
          .unwrap()
          .then(async (resp) => {
            trackEvent(
              "Successfully validated email code",
              { email: currentEmail },
              "Crypto Withdrawal Modal",
            );
            // verified email code
            setEmailVerified(true);
            dispatch(setIsEmailCodeSuccess(true));
          });
      } catch (error: any) {
        dispatch(setError(error.message));
        trackEvent(
          "Failed to validate email code",
          { email: currentEmail },
          "Crypto Withdrawal Modal",
        );
      }
    }
  };

  const handle2FaChange = async (e: any) => {
    dispatch(setError(""));
    const inputCode = e.target.value;
    if (inputCode?.length <= 6) {
      dispatch(set2FACode(inputCode));
    }
  };

  const handleResendClick = async () => {
    setResendClicked(true);
    if (withdrawalAddress && token?.code && withdrawalAmount) {
      await sendEmail({
        sendWithdrawalEmailCodeDto: {
          cryptoAddress: withdrawalAddress,
          token: token?.code,
          amount: withdrawalAmount.toString(),
          type: "Crypto",
        },
      })
        .unwrap()
        .then(() => {
          trackEvent(
            "Resending Email Verification Code",
            { email: currentEmail },
            "Crypto Withdrawal Modal",
          );
        })
        .catch((error) => {
          trackEvent(
            "Error Trying To Resend Email Verification Code",
            { email: currentEmail },
            "Crypto Withdrawal Modal",
          );
          dispatch(setError(error.message));
        });
    }

    setTimeout(() => {
      setResendClicked(false);
    }, 10000);
  };

  return (
    <Grid container direction="column" py={1} pb={4}>
      <StyledSecurityNoticeTypography>
        As an added layer of security, we have a 2-step process that you will need to complete to
        proceed with your withdrawal request.
      </StyledSecurityNoticeTypography>

      <StyledFormContainer>
        <StyledStepHeaderTypography>Step 1: Email Verification</StyledStepHeaderTypography>
        <StyledExplainerTextTypography>
          We sent a verification code to your registered email address:{" "}
          <span style={{ fontWeight: 600 }}>{currentEmail && truncateEmail(currentEmail)}</span>
        </StyledExplainerTextTypography>
        <FormInput
          id="withdraw-modal-email-code"
          title="Email Verification Code"
          value={emailCode || ""}
          onChange={handleEmailChange}
          disabled={checkEmailSuccess || emailCheckLoading}
          styleType={checkEmailIsError ? InputStyleType.ERROR : InputStyleType.DEFAULT}
          InputAdornment={
            <InputAdornment position="end" sx={{ paddingRight: 2 }}>
              {emailCheckLoading ? (
                <CircularProgress size={20} />
              ) : checkEmailSuccess ? (
                <CheckCircleIcon sx={{ color: "#0BEC9F" }} />
              ) : null}
            </InputAdornment>
          }
        />

        {!emailVerified && (
          <StyledFormHelperText
            style={{
              color: resendClicked ? "#545454" : "#fc4c4c",
              pointerEvents: resendClicked ? "none" : "auto",
              cursor: resendClicked ? "not-allowed" : "pointer",
            }}>
            If you did not receive it, please click{" "}
            <span
              style={{
                textDecoration: "underline",
                pointerEvents: resendClicked ? "none" : "auto",
                cursor: resendClicked ? "not-allowed" : "pointer",
                color: resendClicked ? "#545454" : "#fc4c4c",
                fontSize: "inherit",
                fontFamily: "inherit",
                fontStyle: "inherit",
              }}
              onClick={handleResendClick}>
              here
            </span>{" "}
            to resend
          </StyledFormHelperText>
        )}
        {
          <div
            style={{
              filter: !emailVerified ? "blur(2px)" : "none", // Apply blur effect when emailVerified is false
              pointerEvents: !emailVerified ? "none" : "auto", // Disable pointer events when emailVerified is false
            }}>
            <StyledStepHeaderTypography sx={{ marginTop: "16px" }}>
              Step 2: Two Factor Verification
            </StyledStepHeaderTypography>
            <StyledExplainerTextTypography>
              Enter the 2FA code from your authenticator
            </StyledExplainerTextTypography>
            <FormInput
              id="withdraw-modal-2fa-code"
              title="Two-factor Verification Code"
              value={code2fa || ""}
              onChange={handle2FaChange}
              disabled={!emailVerified}
              styleType={InputStyleType.DEFAULT}
              InputAdornment={
                <InputAdornment position="end" sx={{ paddingRight: 2 }}>
                  {isLoading ? <CircularProgress size={20} /> : null}
                </InputAdornment>
              }
            />
          </div>
        }
      </StyledFormContainer>
    </Grid>
  );
};
